import { useCallback, useReducer } from 'react';

import { SeoContentView } from '@zola/svc-marketplace-ts-types';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import { createAction, createReducer } from '@reduxjs/toolkit';

import { fetchStorefrontSearchResults } from '~/libs/client/api/storefronts';
import { getSeoContentBlockHtml } from '~/libs/universal/seo';
import ApiService from '~/util/apiService';

import { buildBackendVendorSearchRequestFromSearchProperties } from '../../../util/buildSearchRequest';
import createCanonicalUrl, { createSeoCanonicalUrl } from '../../../util/canonicalUrl';
import {
  getSelectedFacetOptionsFromVendorSearch,
  ParseVendorSearchUrlResult,
} from '../../../util/parseVendorSearchUrl';
import type { VendorSearchRequest } from '../types/types';
import { trackVendorSearch } from '../utils/pageProviderHelpers';
import {
  DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES,
  PreAuthSearchResultsPageProviderProps,
  SearchResultPageSearchResult,
  SearchResultsPageContext,
  SearchResultsPageContextValues,
  SearchResultsPageMutators,
} from './SearchResultsPageContext';

export const PRE_AUTH_PAGE_SIZE = 48;

/**
 * Action for when a filter or sort has been applied
 */
const searchResultsPageFilterChange = createAction<{
  parsedUrl: ParseVendorSearchUrlResult;
  searchResults: SearchResultPageSearchResult;
  seoContent: SeoContentView | null;
}>('SEARCH_RESULTS_FILTER_CHANGE');

/**
 * Sets loading to true before executing searches
 */
const setLoading = createAction('SET_LOADING');

/**
 * The reducer is the source of truth for the page. It is a combination of initial values from the
 * server, and updates from the user (dispatched through various actions).
 */
const searchResultsPageState = createReducer<
  Omit<SearchResultsPageContextValues, keyof SearchResultsPageMutators>
>({ ...DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES }, (builder) => {
  builder
    .addCase(searchResultsPageFilterChange, (state, action) => {
      return {
        ...state,
        ...action.payload.parsedUrl,
        ...action.payload,
        loading: false,
      };
    })
    .addCase(setLoading, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
});

export const PreAuthSearchResultsPageProvider: React.FC<PreAuthSearchResultsPageProviderProps> = ({
  children,
  parsedUrl,
  facetsByVendorType,
  searchResults,
  isInHoldoutGroupExperiment,
  identifiedAsVendor,
  ...value
}) => {
  const [state, dispatch] = useReducer(searchResultsPageState, {
    ...DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES,
    ...value,
    ...parsedUrl,
    parsedUrl,
    facetsByVendorType,
    searchResults,
    isInHoldoutGroupExperiment,
    identifiedAsVendor,
  });

  // Trigger the initial product list viewed tracking
  useEffectOnce(() => {
    const vendorSearchRequestForInitialTracking =
      buildBackendVendorSearchRequestFromSearchProperties({
        vendorSearch: parsedUrl.vendorSearch,
        pageSize: PRE_AUTH_PAGE_SIZE,
        shouldInsertMiniQuiz: false,
        shouldInsertBudgetQuiz: false,
        isInHoldoutGroupExperiment,
        identifiedAsVendor,
      });

    trackVendorSearch(
      parsedUrl.vendorSearch,
      vendorSearchRequestForInitialTracking,
      searchResults.vendors
    );
  });

  // Main callback that triggers updates to the page when the filters or page number change
  const setVendorSearch = useCallback(
    (nextSearch: VendorSearchRequest) => {
      // First, set loading so we can provide feedback to the couple (blur effect)
      dispatch(setLoading());

      // Build the search request
      const vendorSearchRequest = buildBackendVendorSearchRequestFromSearchProperties({
        vendorSearch: nextSearch,
        pageSize: PRE_AUTH_PAGE_SIZE,
        shouldInsertMiniQuiz: false,
        shouldInsertBudgetQuiz: false,
        isInHoldoutGroupExperiment,
        identifiedAsVendor,
      });

      // Build a promise to fetch the search results
      const executeSearch = () =>
        fetchStorefrontSearchResults(vendorSearchRequest)
          .then((searchResponse) => ({
            totalHits: searchResponse.totalHits,
            offset: searchResponse.offset,
            vendors: searchResponse.entities,
            shouldInsertMiniQuiz: false,
            shouldInsertBudgetQuiz: false,
          }))
          .then((result) => {
            // Trigger product list viewed tracking for the new results
            trackVendorSearch(nextSearch, vendorSearchRequest, result.vendors);
            return result;
          });

      // Fetch the search results and SEO content
      Promise.all([executeSearch(), getSeoContentBlockHtml(nextSearch, ApiService)])
        .then(([newSearchResults, seoContent]) => {
          const canonicalUrl = createCanonicalUrl(nextSearch, facetsByVendorType);
          const seoCanonicalUrl = createSeoCanonicalUrl(nextSearch, facetsByVendorType);
          dispatch(
            searchResultsPageFilterChange({
              parsedUrl: {
                canonicalUrl,
                seoCanonicalUrl,
                vendorSearch: nextSearch,
                selectedFacetOptions: getSelectedFacetOptionsFromVendorSearch(nextSearch),
              },
              searchResults: newSearchResults,
              seoContent,
            })
          );
        })
        .catch(() => null);
    },
    [facetsByVendorType, identifiedAsVendor, isInHoldoutGroupExperiment]
  );

  return (
    <SearchResultsPageContext.Provider
      value={{
        ...state,
        setVendorSearch,
        updateVendorPreferences: () => null,
        updateWeddingPreferences: () => null,
      }}
    >
      {children}
    </SearchResultsPageContext.Provider>
  );
};
